// @ts-nocheck
/**
 * DescriptorPipeline.ts — V2 descriptor pipeline entry point.
 *
 * loadTemplate → compileTemplate → registry.dispatch → RenderDescriptor[]
 */

import type { ValidationCode, ValidationIssue } from '../../diagnostics';
import type { DataRef } from '../../types/binding';
import { logger } from '../../../../../shims/appStub';
import { loadTemplate } from '../loader';
import type { ItemDefinition } from '../../../../../shims/appStub';
import {
  buildComputedValuesById,
  buildScriptsById,
  getPersistDataRef,
  hydrateComputedResults,
  hydrateExpressionComputedResultsSync,
  type BindingContext,
} from '../binding';
import { createExpressionEngine, type ExpressionEngine } from '../expressions';
import type { CompiledRuntimeNode, CompiledRuntimeTemplate } from '../compiler';
import { createDefaultRegistry } from '../renderer/defaultRenderers';
import { compileTemplateCached, invalidateCompileTemplateCache } from './compileTemplateCache';
import type { RenderDescriptor } from '../renderer/types';
import { findElementById, resolveActiveLayoutId } from '../../../utils/templateDocumentUtils';
import type { TemplateDocument, ElementDefinition } from '../../types/canonical';
import { buildBuiltinLayoutDescriptors } from './builtinLayoutDescriptors';
import type { ItemMetricsAdapter } from '../metrics/types';
import { ASYNC_ITEM_METRIC_KEYS } from '../metrics/types';
import { resolveItemMetricsBatch, buildSyncItemMetricCache } from '../metrics/ItemMetricsService';
import {
  collectAllPipelineMetricRefs,
  listCompiledNodesForElement,
} from './collectPipelineMetricRefs';
import { evaluateFieldBindings } from './evaluateFieldBindings';
import { applySchemeItemColorToLayoutDescriptors, collectSchemeDrivenBackgroundNodeIds } from './applySchemeItemColorToLayoutDescriptors';
import type { MutationRecord } from '../action/types';
import {
  collectItemFromKeysFromCompiledNodes,
  prefetchRelatedItemsById,
  resolveRelatedItemIdsForItemFromKeys,
} from '../binding/relatedItemBinding';

/** Prefer explicit layoutId (e.g. placeholder layout); else cyclable index. */
export function resolvePipelineLayoutId(
  element: ElementDefinition | undefined,
  options?: Pick<PipelineOptions, 'layoutIndex' | 'layoutId'>,
): string | undefined {
  if (options?.layoutId) {
    const found = element?.layouts?.find((layout) => layout.id === options.layoutId);
    if (found) {
      return found.id;
    }
  }
  return resolveActiveLayoutId(element, options?.layoutIndex ?? 0);
}

interface FieldBindingPreludeResult {
  pipelineItem: ItemDefinition;
  pendingMutations?: MutationRecord[];
  bindingContext: BindingContext;
  expressionEngine: ExpressionEngine | null;
}

function runFieldBindingPrelude(
  item: ItemDefinition,
  compiled: CompiledRuntimeTemplate,
  targetElement: ElementDefinition | undefined,
  targetElementId: string,
  activeLayoutId: string | undefined,
  document: TemplateDocument,
  options?: PipelineOptions,
): FieldBindingPreludeResult {
  const expressionLibrary = document.scripts?.expressions ?? [];
  const expressionEngine = expressionLibrary.length > 0 ? createExpressionEngine(expressionLibrary) : null;
  const scriptsById = buildScriptsById(document.scripts?.scripts);
  const fieldBindings = targetElement?.scripts?.fieldBindings?.bindings ?? [];
  const layoutNodes = listCompiledNodesForElement(compiled, targetElementId, activeLayoutId);
  const computedRoots = (targetElement?.scripts?.computedValues ?? [])
    .filter((cv) => cv.type === 'expression' && cv.expression?.root)
    .map((cv) => cv.expression!.root as Record<string, unknown>);
  const metricKeys = collectAllPipelineMetricRefs({
    fieldBindings,
    layoutNodes,
    expressions: expressionLibrary,
    computedExpressionRoots: computedRoots,
  });

  let itemMetricCache = options?.itemMetricCache;
  if (metricKeys.length > 0 && itemMetricCache == null) {
    itemMetricCache = buildSyncItemMetricCache(item, metricKeys);
  }

  let pipelineItem = item;
  let pendingMutations: MutationRecord[] | undefined;

  let bindingContext: BindingContext = {
    item: pipelineItem,
    compiledTemplate: compiled,
    elementId: targetElementId,
    computedValuesById: buildComputedValuesById(targetElement?.scripts?.computedValues),
    expressionEngine,
    scriptsById,
    computedResults: options?.computedResults,
    itemMetricCache,
    hostContext: options?.hostContext,
    themeColorPalettes: options?.themeColorPalettes,
    themeColorMode: options?.themeColorMode ?? document.themeColorMode,
    localState: options?.localState,
    relatedItemsById: options?.relatedItemsById,
    templateDocument: document,
  };

  if (!options?.skipFieldBindings && fieldBindings.length > 0) {
    const fieldResult = evaluateFieldBindings(
      fieldBindings,
      item,
      bindingContext,
      expressionLibrary,
    );
    pipelineItem = fieldResult.workingItem;
    if (fieldResult.mutations.length > 0) {
      pendingMutations = fieldResult.mutations;
    }
    bindingContext = { ...bindingContext, item: pipelineItem };
  }

  return {
    pipelineItem,
    pendingMutations,
    bindingContext,
    expressionEngine,
  };
}

export interface PipelineResult {
  descriptors: RenderDescriptor[];
  diagnostics: ValidationIssue[];
  supported: boolean;
  failureReason?: string;
  /** DataRef used for read binding on each node (for inline edit write-back). */
  readBindingByNodeId?: Record<string, DataRef>;
  /** Field-binding mutations to persist (change-only). */
  pendingMutations?: MutationRecord[];
  /** Item used for descriptor dispatch after field-binding merge. */
  pipelineItem?: ItemDefinition;
}

export interface PipelineOptions {
  layoutIndex?: number;
  /**
   * When set, selects this layout id directly (bypasses cyclable layoutIndex).
   * Used for placeholder layouts (DEC-062) which are excluded from layoutInUse cycling.
   */
  layoutId?: string;
  elementId?: string;
  /** Pre-hydrated script computeds (optional). */
  computedResults?: Record<string, unknown>;
  /** When true, awaits script-backed computed hydration before dispatch. */
  hydrateScriptComputeds?: boolean;
  /** Host adapter for lazy item metrics (async path). */
  itemMetricsAdapter?: ItemMetricsAdapter | null;
  /** Pre-resolved item metric cache (sync path or async prelude). */
  itemMetricCache?: BindingContext['itemMetricCache'];
  /** Skip field-binding evaluation (tests). */
  skipFieldBindings?: boolean;
  /** Host-supplied runtime context for expression/binding resolution (DEC-044). */
  hostContext?: BindingContext['hostContext'];
  /** Host-supplied app theme palettes (DEC-047). */
  themeColorPalettes?: BindingContext['themeColorPalettes'];
  /** Template-level default theme color mode override (DEC-047). */
  themeColorMode?: BindingContext['themeColorMode'];
  /** Ephemeral localState session bag (DEC-061). */
  localState?: BindingContext['localState'];
  /**
   * Prefetched items for DEC-074 categoryProperty.itemFrom (sync paint).
   * Prefer populating via `fetchItemById` on the async pipeline.
   */
  relatedItemsById?: BindingContext['relatedItemsById'];
  /** Host loader for related items referenced by itemFrom itemRefs (DEC-074). */
  fetchItemById?: (itemId: number) => Promise<ItemDefinition | null | undefined>;
  /** Active UI locale for LocalizedText display channels. */
  locale?: string;
}

export function pipelineSucceeded(result: PipelineResult | null): boolean {
  return result !== null && result.supported === true && result.descriptors.length > 0;
}

/** @deprecated Use pipelineSucceeded */
export const shouldUseV2Path = pipelineSucceeded;

function hasBlockingIssues(issues: ValidationIssue[]): boolean {
  return issues.some((i) => i.severity === 'error');
}

let sharedDefaultRegistry: ReturnType<typeof createDefaultRegistry> | null = null;

function getSharedDefaultRegistry(): ReturnType<typeof createDefaultRegistry> {
  if (!sharedDefaultRegistry) {
    sharedDefaultRegistry = createDefaultRegistry();
  }
  return sharedDefaultRegistry;
}

export { invalidateCompileTemplateCache };

/** @internal Test helper — clears module compile/registry caches. */
export function resetDescriptorPipelineCachesForTests(): void {
  invalidateCompileTemplateCache();
  sharedDefaultRegistry = null;
}

/** True when the pipeline must run the async prelude (scripts, field bindings, or async item metrics). */
export function needsAsyncDescriptorPipeline(
  template: TemplateDocument,
  element?: ElementDefinition,
  layoutIndexOrOptions: number | Pick<PipelineOptions, 'layoutIndex' | 'layoutId'> = 0,
): boolean {
  const expressions = template.scripts?.expressions?.length ?? 0;
  const scripts = template.scripts?.scripts?.length ?? 0;
  const fieldBindings = element?.scripts?.fieldBindings?.bindings?.length ?? 0;
  if (expressions > 0 || scripts > 0 || fieldBindings > 0) {
    return true;
  }

  const loadResult = loadTemplate(template, { allowPartialLoad: true });
  if (!loadResult.loaded) {
    return false;
  }
  const { compiled } = compileTemplateCached(template, loadResult.loaded);
  if (!compiled) {
    return false;
  }

  const elementId = element?.id ?? compiled.primaryElementId;
  const targetElement = element ?? findElementById(loadResult.loaded.document, elementId);
  const layoutOptions =
    typeof layoutIndexOrOptions === 'number'
      ? { layoutIndex: layoutIndexOrOptions }
      : layoutIndexOrOptions;
  const activeLayoutId = resolvePipelineLayoutId(targetElement, layoutOptions);
  const layoutNodes = listCompiledNodesForElement(compiled, elementId, activeLayoutId);
  const computedRoots = (targetElement?.scripts?.computedValues ?? [])
    .filter((cv) => cv.type === 'expression' && cv.expression?.root)
    .map((cv) => cv.expression!.root as Record<string, unknown>);

  const metricKeys = collectAllPipelineMetricRefs({
    fieldBindings: targetElement?.scripts?.fieldBindings?.bindings,
    layoutNodes,
    expressions: template.scripts?.expressions,
    computedExpressionRoots: computedRoots,
  });
  if (collectItemFromKeysFromCompiledNodes(layoutNodes).length > 0) {
    return true;
  }
  if (metricKeys.length === 0) {
    return false;
  }

  const asyncSet = new Set(ASYNC_ITEM_METRIC_KEYS);
  return metricKeys.some((key) => asyncSet.has(key));
}

function dispatchNode(
  node: CompiledRuntimeNode,
  compiled: CompiledRuntimeTemplate,
  item: ItemDefinition,
  readBindingByNodeId: Record<string, DataRef>,
  bindingContext: BindingContext,
  expressionEngine: ExpressionEngine | null,
  locale: string | undefined,
): RenderDescriptor {
  const persistRef = getPersistDataRef(node);
  if (persistRef) {
    readBindingByNodeId[node.id] = persistRef;
  }

  const registry = getSharedDefaultRegistry();
  const childIds: string[] = compiled.indexes.childNodeIdsByParentId[node.id] ?? [];

  const childRenderer = (childNodeKey: string): RenderDescriptor | null => {
    const childNode = compiled.nodesById[childNodeKey];
    if (!childNode) return null;
    return dispatchNode(
      childNode,
      compiled,
      item,
      readBindingByNodeId,
      bindingContext,
      expressionEngine,
      locale,
    );
  };

  return registry.dispatch(
    {
      node,
      visible: true,
      enabled: true,
      issues: [],
      compiledTemplate: compiled,
      bindingContext,
      expressionEngine,
      locale,
    },
    childRenderer,
  );
}

export function runDescriptorPipeline(
  document: TemplateDocument,
  item: ItemDefinition,
  options?: PipelineOptions,
): PipelineResult {
  const allDiagnostics: ValidationIssue[] = [];

  // Match PreviewPipeline: semantic issues (e.g. undeclared script capabilities on
  // class library scripts merged into document.scripts) must not blank the canvas.
  const loadResult = loadTemplate(document, { allowPartialLoad: true });
  for (const issue of loadResult.validationResult.issues) {
    allDiagnostics.push(issue);
  }

  if (!loadResult.loaded) {
    logger.warn('DescriptorPipeline', 'template failed to load or validate', {
      templateId: document.metadata?.templateId,
      elementId: options?.elementId,
      layoutIndex: options?.layoutIndex,
      layoutId: options?.layoutId,
      issues: (loadResult.validationResult.issues ?? []).slice(0, 12).map((d) => ({
        code: d.code,
        severity: d.severity,
        message: d.message,
        path: d.path,
      })),
    });
    return {
      descriptors: [],
      diagnostics: allDiagnostics,
      supported: false,
      failureReason: 'template failed to load or validate',
    };
  }

  const { compiled, issues: compileIssues } = compileTemplateCached(document, loadResult.loaded);
  for (const issue of compileIssues) {
    allDiagnostics.push(issue);
  }

  if (!compiled || hasBlockingIssues(compileIssues)) {
    return {
      descriptors: [],
      diagnostics: allDiagnostics,
      supported: false,
      failureReason: 'compile stage produced blocking errors',
    };
  }

  const targetElementId = options?.elementId ?? compiled.primaryElementId;
  const targetElement = findElementById(loadResult.loaded.document, targetElementId);
  const activeLayoutId = resolvePipelineLayoutId(targetElement, options);

  const activeLayout =
    activeLayoutId !== undefined
      ? targetElement?.layouts.find((layout) => layout.id === activeLayoutId)
      : undefined;

  const prelude = runFieldBindingPrelude(
    item,
    compiled,
    targetElement,
    targetElementId,
    activeLayoutId,
    loadResult.loaded.document,
    options,
  );

  if (activeLayout?.renderAsBuiltin === true && activeLayoutId !== undefined) {
    const descriptors = buildBuiltinLayoutDescriptors(prelude.pipelineItem, activeLayoutId);
    return {
      descriptors,
      diagnostics: allDiagnostics,
      supported: descriptors.length > 0,
      failureReason: descriptors.length === 0 ? 'no builtin descriptors' : undefined,
      pendingMutations: prelude.pendingMutations,
      pipelineItem: prelude.pipelineItem,
    };
  }

  const childSet = new Set(Object.values(compiled.indexes.childNodeIdsByParentId).flat());

  const topLevelNodes = Object.values(compiled.nodesById).filter((node) => {
    if (node.elementId !== targetElementId || childSet.has(node.id)) {
      return false;
    }
    if (activeLayoutId !== undefined) {
      return node.layoutId === activeLayoutId;
    }
    return true;
  });

  if (topLevelNodes.length === 0) {
    if (!targetElement || activeLayoutId === undefined) {
      return {
        descriptors: [],
        diagnostics: allDiagnostics,
        supported: false,
        failureReason: 'element has no top-level renderable nodes',
      };
    }
    return {
      descriptors: [],
      diagnostics: allDiagnostics,
      supported: true,
      pendingMutations: prelude.pendingMutations,
      pipelineItem: prelude.pipelineItem,
    };
  }

  const readBindingByNodeId: Record<string, DataRef> = {};
  let { pipelineItem, pendingMutations, bindingContext, expressionEngine } = prelude;

  const expressionOnly = hydrateExpressionComputedResultsSync(bindingContext);
  bindingContext = {
    ...bindingContext,
    computedResults: { ...expressionOnly, ...(options?.computedResults ?? {}) },
  };

  const rawDescriptors: RenderDescriptor[] = topLevelNodes.map((node) =>
    dispatchNode(
      node,
      compiled,
      pipelineItem,
      readBindingByNodeId,
      bindingContext,
      expressionEngine,
      options?.locale,
    ),
  );
  const layoutNodes = listCompiledNodesForElement(compiled, targetElementId, activeLayoutId);
  const schemeDrivenNodeIds = collectSchemeDrivenBackgroundNodeIds(layoutNodes);
  const descriptors = applySchemeItemColorToLayoutDescriptors(
    rawDescriptors,
    pipelineItem,
    schemeDrivenNodeIds,
  );

  return {
    descriptors,
    diagnostics: allDiagnostics,
    supported: descriptors.length > 0,
    failureReason: descriptors.length === 0 ? 'no renderable descriptors' : undefined,
    readBindingByNodeId,
    pendingMutations,
    pipelineItem,
  };
}

/** Async pipeline entry — hydrates script-backed computed values and item metrics before dispatch. */
export async function runDescriptorPipelineAsync(
  document: TemplateDocument,
  item: ItemDefinition,
  options?: PipelineOptions,
): Promise<PipelineResult> {
  const expressionLibrary = document.scripts?.expressions ?? [];
  const expressionEngine = expressionLibrary.length > 0 ? createExpressionEngine(expressionLibrary) : null;
  const targetElementId = options?.elementId;
  const loadResult = loadTemplate(document, { allowPartialLoad: true });
  if (!loadResult.loaded) {
    return runDescriptorPipeline(document, item, options);
  }
  const { compiled } = compileTemplateCached(document, loadResult.loaded);
  if (!compiled) {
    return runDescriptorPipeline(document, item, options);
  }
  const elementId = targetElementId ?? compiled.primaryElementId;
  const targetElement = findElementById(loadResult.loaded.document, elementId);
  const activeLayoutId = resolvePipelineLayoutId(targetElement, options);
  const fieldBindings = targetElement?.scripts?.fieldBindings?.bindings ?? [];
  const layoutNodes = listCompiledNodesForElement(compiled, elementId, activeLayoutId);
  const computedRoots = (targetElement?.scripts?.computedValues ?? [])
    .filter((cv) => cv.type === 'expression' && cv.expression?.root)
    .map((cv) => cv.expression!.root as Record<string, unknown>);

  const metricKeys = collectAllPipelineMetricRefs({
    fieldBindings,
    layoutNodes,
    expressions: expressionLibrary,
    computedExpressionRoots: computedRoots,
  });

  let itemMetricCache = options?.itemMetricCache;
  if (metricKeys.length > 0 && itemMetricCache == null) {
    itemMetricCache = await resolveItemMetricsBatch(
      item,
      metricKeys,
      options?.itemMetricsAdapter,
      { categoryIds: targetElement?.categoryIds ?? item.categories },
    );
  }

  let relatedItemsById = options?.relatedItemsById;
  if (relatedItemsById == null && options?.fetchItemById) {
    const fromKeys = collectItemFromKeysFromCompiledNodes(layoutNodes);
    const relatedIds = resolveRelatedItemIdsForItemFromKeys(item, fromKeys);
    if (relatedIds.length > 0) {
      relatedItemsById = await prefetchRelatedItemsById(relatedIds, options.fetchItemById);
    }
  }

  const baseContext: BindingContext = {
    item,
    compiledTemplate: compiled,
    elementId,
    computedValuesById: buildComputedValuesById(targetElement?.scripts?.computedValues),
    expressionEngine,
    scriptsById: buildScriptsById(document.scripts?.scripts),
    computedResults: options?.computedResults,
    itemMetricCache,
    hostContext: options?.hostContext,
    themeColorPalettes: options?.themeColorPalettes,
    themeColorMode: options?.themeColorMode ?? document.themeColorMode,
    localState: options?.localState,
    relatedItemsById,
    templateDocument: document,
  };
  const hydrated = await hydrateComputedResults(baseContext);
  return runDescriptorPipeline(document, item, {
    ...options,
    elementId,
    computedResults: hydrated,
    itemMetricCache,
    relatedItemsById,
  });
}
